import { t } from "@lingui/core/macro"
import { Link, Outlet, createFileRoute, useLocation } from "@tanstack/react-router"
import {
  Ruler,
  Settings,
  Settings2,
  Store,
  Building2,
  Layers,
  History,
  Users,
  Share2,
  Flag,
  Globe,
} from "lucide-react"
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarRail,
  SidebarTrigger,
} from "@/components/ui/sidebar"
import { useTypedFlag } from "@/features/feature-flags/registry"
import { usePermissions } from "@/hooks/usePermissions"

export const Route = createFileRoute("/(authenticated)/settings")({
  component: SettingsLayoutComponent,
})

type NavigationItem = {
  title: string
  href: string
  icon: React.ComponentType
}

type NavigationSection = {
  title: string
  href?: string
  icon?: React.ComponentType
  items?: NavigationItem[]
}

function getSettingsNavigation(
  canViewAuditLog: boolean,
  canManageConfigVersions: boolean,
  canManageFeatureFlags: boolean,
  canManageCountryCodeMappings: boolean,
  canManageStoreSizeBuckets: boolean,
  canManageStandardStore: boolean,
  canManageShelfTypeOrder: boolean,
  showFiles: boolean,
  isAdmin: boolean,
): NavigationSection[] {
  const items: NavigationSection[] = [
    {
      title: t`Overview`,
      href: "/settings",
      icon: Settings,
    },
  ]

  // Only show Configuration Versions for users who can update them (not store_manager)
  if (canManageConfigVersions) {
    items.push({
      title: t`Configuration Versions`,
      href: "/settings/configuration-versions",
      icon: Settings2,
    })
  }

  // Only show Country Code Mappings for users who can manage them
  if (canManageCountryCodeMappings) {
    items.push({
      title: t`Country Code Mappings`,
      href: "/settings/country-code-mappings",
      icon: Globe,
    })
  }

  // Only show Store Sizes for users who can edit the rack-count thresholds
  if (canManageStoreSizeBuckets) {
    items.push({
      title: t`Store Sizes`,
      href: "/settings/store-sizes",
      icon: Ruler,
    })
  }

  if (canManageStandardStore) {
    items.push({
      title: t`Standard store`,
      href: "/settings/standard-store",
      icon: Building2,
    })
  }

  // Shelf-type order — planner-controlled ordering of overlay shelf-types in reports
  if (canManageShelfTypeOrder) {
    items.push({
      title: t`Shelf-type order`,
      href: "/settings/shelf-types",
      icon: Layers,
    })
  }

  items.push({
    title: t`Stores`,
    href: "/settings/stores",
    icon: Store,
  })

  // File sharing is part of the Files feature surface.
  if (showFiles) {
    items.push({
      title: t`Shared Links`,
      href: "/settings/shares",
      icon: Share2,
    })
  }

  // Only show audit log for users with auditLog:read permission (admin, blueprint_planner)
  if (canViewAuditLog) {
    items.push({
      title: t`Audit log`,
      href: "/settings/audit",
      icon: History,
    })
  }

  // Admin-only: Feature Flags
  if (canManageFeatureFlags) {
    items.push({
      title: t`Feature Flags`,
      href: "/settings/feature-flags",
      icon: Flag,
    })
  }

  // Admin-only: User management
  if (isAdmin) {
    items.push({
      title: t`Users`,
      href: "/settings/users",
      icon: Users,
    })
  }

  return items
}

function SettingsLayoutComponent() {
  const location = useLocation()
  const { isAdmin, hasPermission } = usePermissions()
  const canViewAuditLog = hasPermission("auditLog", "read").allowed
  const canManageConfigVersions = hasPermission("configVersion", "update").allowed
  const canManageFeatureFlags = hasPermission("featureFlag", "read").allowed
  const canManageCountryCodeMappings = hasPermission("countryCodeMapping", "update").allowed
  const canManageStoreSizeBuckets = hasPermission("storeSizeBucket", "update").allowed
  const canManageStandardStore = hasPermission("standardStore", "update").allowed
  const canManageShelfTypeOrder = hasPermission("shelfTypeOrder", "update").allowed
  const { value: showFiles } = useTypedFlag("show-files")
  const settingsNavigation = getSettingsNavigation(
    canViewAuditLog,
    canManageConfigVersions,
    canManageFeatureFlags,
    canManageCountryCodeMappings,
    canManageStoreSizeBuckets,
    canManageStandardStore,
    canManageShelfTypeOrder,
    showFiles,
    isAdmin,
  )

  return (
    <SidebarProvider className="h-full !min-h-0">
      <div className="relative flex h-full w-full overflow-hidden">
        <Sidebar variant="inset">
          <SidebarHeader>
            <div className="flex items-center gap-2 px-2 py-1">
              <Settings className="text-sidebar-primary size-6" />
              <span className="font-serif text-xl font-bold">{t`Settings`}</span>
            </div>
          </SidebarHeader>

          <SidebarContent>
            <SidebarGroup>
              <SidebarGroupLabel>Configuration</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  {settingsNavigation.map((section: NavigationSection) => (
                    <div key={section.title}>
                      {section.href ? (
                        <SidebarMenuItem>
                          <SidebarMenuButton
                            asChild
                            isActive={
                              section.href === "/settings"
                                ? location.pathname === section.href
                                : location.pathname.startsWith(section.href)
                            }
                          >
                            <Link to={section.href}>
                              {section.icon && <section.icon />}
                              <span>{section.title}</span>
                            </Link>
                          </SidebarMenuButton>
                        </SidebarMenuItem>
                      ) : (
                        <>
                          <SidebarMenuItem>
                            <div className="text-sidebar-foreground/70 px-2 py-1 text-xs font-medium">
                              {section.title}
                            </div>
                          </SidebarMenuItem>
                          {section.items?.map((item: NavigationItem) => (
                            <SidebarMenuItem key={item.href}>
                              <SidebarMenuButton asChild isActive={location.pathname === item.href}>
                                <Link to={item.href}>
                                  <item.icon />
                                  <span>{item.title}</span>
                                </Link>
                              </SidebarMenuButton>
                            </SidebarMenuItem>
                          ))}
                        </>
                      )}
                    </div>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </SidebarContent>

          <SidebarRail />
        </Sidebar>

        <SidebarInset className="dark:bg-brand-darkblue-900 min-w-0 bg-white">
          {/* Same horizontal padding as the content below so the trigger lines up with page titles */}
          <header className="flex shrink-0 items-center gap-2 px-4 py-4 sm:px-8">
            {/* -ml-1.5 cancels the button's inner padding so the icon sits on the content edge */}
            <SidebarTrigger className="-ml-1.5" />
          </header>

          <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden px-4 sm:px-8">
            <Outlet />
          </div>
        </SidebarInset>
      </div>
    </SidebarProvider>
  )
}
